import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Grid, Paper, Stack, Text, useMantineTheme } from "@mantine/core";
import { DashedBarChart, NpsScoreCard, RatingBarChart } from "@testuj.to/react";
import { CampaignProductResults } from "src/graphql/generated/graphql";

export interface OverviewTabProps {
    productsResults: CampaignProductResults[];
}

export const OverviewTab = ({ productsResults }: OverviewTabProps) => {
    const { t } = useTranslation(["common"]);
    const theme = useMantineTheme();
    const [searchParams] = useSearchParams();
    const productId = searchParams.get("productId");

    const productResults = useMemo(() => {
        return (productsResults || []).find((item) => item.productId === productId) || productsResults?.[0];
    }, [productsResults, productId]);

    const recommendationColor = useMemo(() => {
        if (productResults?.wouldRecommendPercent === undefined) {
            return "grey";
        }

        if (productResults?.wouldRecommendPercent >= 80) {
            return "#029218";
        }

        if (productResults?.wouldRecommendPercent >= 50) {
            return "#fab453";
        }

        return "#ff001e";
    }, [productResults?.wouldRecommendPercent]);

    const ratingBarChartItems = useMemo(() => {
        const defaultRatingBrakup = new Array(5).fill(0).map((_, index) => {
            return {
                label: `${index + 1} `,
                value: 0,
            };
        });

        const roundedRatings = productResults?.totalRatingBreakup?.reduce(
            (acc, { rating, count }) => {
                const roundedRating = Math.max(1, Math.round(rating / 2));
                acc[roundedRating - 1].value += count;
                return acc;
            },
            [...defaultRatingBrakup]
        );

        return roundedRatings?.reverse() || [];
    }, [productResults?.totalRatingBreakup]);

    const npsCoreChartValues = useMemo(() => {
        const promoters = productResults?.totalRatingBreakup?.reduce((acc, { rating, count }) => {
            if (rating >= 9) {
                acc += count;
            }
            return acc;
        }, 0);

        const passives = productResults?.totalRatingBreakup?.reduce((acc, { rating, count }) => {
            if (rating >= 7 && rating <= 8) {
                acc += count;
            }
            return acc;
        }, 0);

        const detractors = productResults?.totalRatingBreakup?.reduce((acc, { rating, count }) => {
            if (rating <= 6) {
                acc += count;
            }
            return acc;
        }, 0);

        return {
            promoters,
            passives,
            detractors,
        };
    }, [productResults?.totalRatingBreakup]);

    if (!productResults) {
        return (
            <Grid w="100%">
                <div>No results </div>
            </Grid>
        );
    }

    return (
        <Grid w="100%">
            <Grid.Col lg={6} xl={3}>
                <Stack
                    align="center"
                    justify="center"
                    spacing={0}
                    py={32}
                    px={64}
                    style={{
                        height: "100%",
                        borderRadius: 12,
                        backgroundColor: theme.fn.lighten(recommendationColor, 0.88),
                    }}
                >
                    <Text weight="bold" size={60} color={recommendationColor} align="center">
                        {productResults?.wouldRecommendPercent.toFixed(0)}%
                    </Text>
                    <Text weight={600} color={recommendationColor} align="center">
                        {t("campaign_overview_customers_recommend")}
                    </Text>
                </Stack>
            </Grid.Col>
            <Grid.Col lg={6} xl={3}>
                <Paper p={28} radius={12} style={{ height: "100%" }}>
                    <Text mb={24} weight={600}>
                        {t("campaign_overview_popularity_measuring_rating")}
                    </Text>
                    <RatingBarChart items={ratingBarChartItems} />
                </Paper>
            </Grid.Col>
            <Grid.Col lg={6}>
                <Paper radius={12} style={{ height: "100%" }}>
                    <NpsScoreCard
                        promoters={npsCoreChartValues?.promoters || 0}
                        passives={npsCoreChartValues?.passives || 0}
                        detractors={npsCoreChartValues?.detractors || 0}
                        t={t}
                    />
                </Paper>
            </Grid.Col>
            {/* TODO: Zatím bez parametrů - podle dodaných informací je tato sekce zbytečná */}
            {/* <Grid.Col lg={6}>
                <Paper p={28} radius={12} style={{ height: "100%" }}>
                    <Text mb={24} weight={600}>
                        {t("campaign_detail.most_frequent_properties")}
                    </Text>
                    <Grid>
                        <Grid.Col md={6}>
                            <Flex gap={8}>
                                <ThemeIcon color="#029218" radius="xl">
                                    <IconPlus size={14} />
                                </ThemeIcon>
                                Přírodní složky
                            </Flex>
                        </Grid.Col>
                        <Grid.Col md={6}>
                            <Flex gap={8}>
                                <ThemeIcon color="#FF001D" radius="xl">
                                    <IconMinus size={14} />
                                </ThemeIcon>
                                Vysoká cena
                            </Flex>
                        </Grid.Col>
                    </Grid>
                </Paper>
            </Grid.Col> */}
            <Grid.Col lg={12} xl={6}>
                <Paper p={28} radius={12} style={{ height: "100%" }}>
                    <Text mb={24} weight={600}>
                        {t("campaign_overview_product_properties")}
                    </Text>
                    <DashedBarChart
                        items={
                            (productResults?.attributeRatings || []).map((attribute) => ({
                                label: attribute.name,
                                value: attribute.rating * 10,
                            })) || []
                        }
                    />
                </Paper>
            </Grid.Col>
        </Grid>
    );
};
